import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { getBookApi, addChapterApi, updateChapterApi, deleteChapterApi } from '../../utils/api';

const EMPTY = { title:'', content:'', chapterNumber:'', isFree:'false', price:'0' };

export default function AdminChapters() {
  const { id } = useParams();
  const [book,     setBook]     = useState(null);
  const [chapters, setChapters] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing,  setEditing]  = useState(null);
  const [form,     setForm]     = useState(EMPTY);
  const [busy,     setBusy]     = useState(false);
  const [error,    setError]    = useState('');
  const [toast,    setToast]    = useState('');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const load = () => {
    setLoading(true);
    getBookApi(id)
      .then(({ data }) => {
        setBook(data);
        setChapters([...(data.chapters || [])].sort((a, b) => a.chapterNumber - b.chapterNumber));
      })
      .finally(() => setLoading(false));
  };

  useEffect(load, [id]);

  const openAdd = () => {
    setForm({ ...EMPTY, chapterNumber: String(chapters.length + 1) });
    setEditing(null);
    setShowForm(true);
    setError('');
  };

  const openEdit = (ch) => {
    setForm({
      title: ch.title,
      content: ch.content || '',
      chapterNumber: String(ch.chapterNumber),
      isFree: ch.isFree ? 'true' : 'false',
      price: String(ch.price || 0),
    });
    setEditing(ch);
    setShowForm(true);
    setError('');
  };

  const closeForm = () => { setShowForm(false); setEditing(null); setError(''); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      if (editing) {
        await updateChapterApi(editing._id, form);
        showToast('✅ Chapter updated!');
      } else {
        await addChapterApi(id, form);
        showToast('✅ Chapter added!');
      }
      load();
      closeForm();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save');
    } finally {
      setBusy(false);
    }
  };

  const del = async (chId, chTitle) => {
    if (!window.confirm(`Delete chapter "${chTitle}"?`)) return;
    try {
      await deleteChapterApi(chId);
      setChapters(prev => prev.filter(c => c._id !== chId));
      showToast('Chapter deleted.');
    } catch {
      alert('Failed to delete');
    }
  };

  const wordCount = form.content.trim() ? form.content.trim().split(/\s+/).length : 0;

  if (loading) return <AdminLayout title="Chapters"><div className="spinner" /></AdminLayout>;

  return (
    <AdminLayout title={`Chapters — ${book?.title || '…'}`}>
      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 24, right: 24, zIndex: 9999,
          background: 'var(--success)', color: '#fff',
          padding: '12px 24px', borderRadius: 10,
          fontWeight: 700, fontSize: '0.88rem',
          boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          animation: 'fadeUp 0.3s ease both',
        }}>{toast}</div>
      )}

      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24, color: 'var(--text3)', fontSize: '0.82rem' }}>
        <Link to="/admin/books" style={{ color: 'var(--gold)', fontWeight: 700, textDecoration: 'none' }}>← All Books</Link>
        <span>/</span>
        <span>{book?.title}</span>
      </div>

      {/* Stats row */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        {[
          ['📄', chapters.length, 'Chapters'],
          ['🆓', chapters.filter(c => c.isFree).length, 'Free'],
          ['🔒', chapters.filter(c => !c.isFree).length, 'Paid'],
          ['👁', chapters.reduce((s, c) => s + (c.readCount || 0), 0).toLocaleString(), 'Total Reads'],
          ['♥', chapters.reduce((s, c) => s + (c.likeCount || 0), 0).toLocaleString(), 'Total Likes'],
        ].map(([icon, val, label]) => (
          <div key={label} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: '1.2rem' }}>{icon}</span>
            <div>
              <div style={{ fontFamily: 'var(--font-d)', fontSize: '1.3rem', fontWeight: 700, color: 'var(--text)', lineHeight: 1 }}>{val}</div>
              <div style={{ color: 'var(--text3)', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 700 }}>{label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Chapter list */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 24px', borderBottom: '1px solid var(--border)' }}>
          <h2 style={{ fontFamily: 'var(--font-d)', fontSize: '1.2rem', color: 'var(--text)' }}>{chapters.length} Chapters</h2>
          <button className="btn btn-gold btn-sm" onClick={openAdd}>+ Add Chapter</button>
        </div>

        {chapters.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center', color: 'var(--text3)' }}>
            <p style={{ fontSize: '2rem', marginBottom: 12 }}>📝</p>
            <p>No chapters yet. Add your first one!</p>
          </div>
        ) : (
          chapters.map((ch, i) => (
            <div key={ch._id} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '16px 24px',
              borderBottom: i < chapters.length - 1 ? '1px solid var(--border)' : 'none',
              background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.01)',
            }}>
              <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                <span style={{ fontFamily: 'var(--font-d)', fontWeight: 700, color: 'var(--gold)', fontSize: '0.88rem', minWidth: 44 }}>
                  Ch.{ch.chapterNumber}
                </span>
                <div>
                  <p style={{ fontWeight: 700, color: 'var(--text)', fontSize: '0.9rem' }}>{ch.title}</p>
                  <p style={{ color: 'var(--text3)', fontSize: '0.75rem', marginTop: 3, display: 'flex', gap: 14 }}>
                    <span>{(ch.wordCount || 0).toLocaleString()} words</span>
                    <span>👁 {(ch.readCount || 0).toLocaleString()} reads</span>
                    <span>♥ {(ch.likeCount || 0).toLocaleString()} likes</span>
                    <span>{ch.isFree
                      ? <span style={{ color: 'var(--success)', fontWeight: 700 }}>Free</span>
                      : <span style={{ color: 'var(--accent)' }}>Paid {ch.price > 0 ? `₹${ch.price}` : ''}</span>}
                    </span>
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-dark btn-sm" onClick={() => openEdit(ch)}>Edit</button>
                <button className="btn btn-danger btn-sm" onClick={() => del(ch._id, ch.title)}>Del</button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add / Edit modal */}
      {showForm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
          zIndex: 1000, display: 'flex', alignItems: 'flex-start',
          justifyContent: 'center', padding: '32px 16px',
          overflowY: 'auto', backdropFilter: 'blur(4px)',
        }}>
          <div style={{
            background: 'var(--bg2)', border: '1px solid var(--border2)',
            borderRadius: 'var(--radius-lg)', padding: '32px',
            width: '100%', maxWidth: 800,
            boxShadow: '0 40px 80px rgba(0,0,0,0.6)',
          }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h3 style={{ fontFamily: 'var(--font-d)', fontSize: '1.5rem', color: 'var(--text)' }}>
                {editing ? `Edit Chapter ${editing.chapterNumber}` : 'Add New Chapter'}
              </h3>
              <button onClick={closeForm} style={{ background: 'none', border: 'none', color: 'var(--text3)', fontSize: '1.2rem', cursor: 'pointer', padding: '4px 8px' }}>✕</button>
            </div>

            {error && <div className="alert alert-error">{error}</div>}

            <form onSubmit={submit}>
              {/* Top row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 4 }}>
                <div className="form-group">
                  <label>Chapter Number *</label>
                  <input className="form-control" type="number" min="1"
                    value={form.chapterNumber}
                    onChange={e => setForm({ ...form, chapterNumber: e.target.value })}
                    required />
                </div>
                <div className="form-group">
                  <label>Access Type</label>
                  <select className="form-control" value={form.isFree}
                    onChange={e => setForm({ ...form, isFree: e.target.value })}>
                    <option value="true">🆓 Free</option>
                    <option value="false">🔒 Paid</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Chapter Price ₹</label>
                  <input className="form-control" type="number" min="0"
                    value={form.price}
                    onChange={e => setForm({ ...form, price: e.target.value })}
                    disabled={form.isFree === 'true'} />
                </div>
              </div>

              <div className="form-group">
                <label>Chapter Title *</label>
                <input className="form-control"
                  value={form.title}
                  onChange={e => setForm({ ...form, title: e.target.value })}
                  placeholder="Enter a compelling chapter title"
                  required />
              </div>

              <div className="form-group">
                <label>Content * — use blank lines to separate paragraphs</label>
                <textarea
                  className="form-control"
                  rows={18}
                  value={form.content}
                  onChange={e => setForm({ ...form, content: e.target.value })}
                  placeholder="Write your chapter content here…&#10;&#10;Each blank line creates a new paragraph."
                  required
                  style={{
                    fontFamily: 'var(--font-d)',
                    fontSize: '1rem',
                    lineHeight: 1.75,
                    minHeight: 380,
                  }}
                />
                <p style={{ color: 'var(--text3)', fontSize: '0.78rem', marginTop: 6, display: 'flex', gap: 16 }}>
                  <span>~{wordCount.toLocaleString()} words</span>
                  <span>~{Math.ceil(wordCount / 250)} min read</span>
                </p>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, paddingTop: 20, borderTop: '1px solid var(--border)' }}>
                <button type="button" className="btn btn-ghost" onClick={closeForm}>Cancel</button>
                <button type="submit" className="btn btn-gold" disabled={busy}>
                  {busy ? 'Saving…' : editing ? 'Update Chapter' : 'Add Chapter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
